import { useEffect, useMemo, useState } from "react";
import { CopyableEmail } from "@/components/common/CopyableEmail";
import { useNavigate } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { ChevronDown, Code, Copy, ExternalLink, RefreshCw, Wand2 } from "lucide-react";
import { toast } from "sonner";
import { SideDrawer } from "@/components/common/SideDrawer";
import { TranscriptCopyButton } from "@/components/conversation/details/TranscriptCopyButton";
import { TranscriptTab } from "@/components/conversation/details/TranscriptTab";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useConversationAnalysis } from "@/hooks/agent/useConversationAnalysis";
import { cn } from "@/lib/utils";
import { ConversationLogsService } from "@/services/communication/ConversationLogsService";
import { CopyableJsonBlock } from "../BrainChatLogs/CopyableJsonBlock";
import { AgentNameCell } from "./AgentNameCell";
import {
  useConversationLogDetails,
  useElevenLabsConversationData,
} from "./hooks/useConversationLogs";

import type { TranscriptLine } from "@/types/conversation/transcript";
import type { ConversationLog, ConversationLogDetail } from "@/types/conversation-logs";

type DetailsTab = "activity" | "transcript";

interface ConversationLogDetailsSidebarProps {
  isOpen: boolean;
  onClose: () => void;
  selectedLog: ConversationLog | null;
  getStatusBadge: (status: string | null) => React.ReactNode;
  showRawDataModal?: boolean;
  onShowRawDataModalChange?: (show: boolean) => void;
}

const getActivityLogStatusBadge = (status: string | null) => {
  const statusColorMap: Record<string, string> = {
    success: "bg-green-100 text-green-700 border-green-200",
    started: "bg-pink-100 text-pink-700 border-pink-200",
    recorded: "bg-blue-100 text-blue-700 border-blue-200",
    completed: "bg-green-100 text-green-700 border-green-200",
    failed: "bg-red-100 text-red-700 border-red-200",
    fail: "bg-red-100 text-red-700 border-red-200",
    skipped: "bg-yellow-100 text-yellow-700 border-yellow-200",
  };

  if (!status) return null;
  const className = statusColorMap[status] || "bg-gray-100 text-gray-700 border-gray-200";
  return <Badge className={cn(className, "border w-fit")}>{status}</Badge>;
};

const HANDOFF_ACTION_PREFIX = "handoff:";
// The dial outcome closes an attempt on Twilio's side, whatever order its row
// was saved in.
const DIAL_RESULT_ACTION = `${HANDOFF_ACTION_PREFIX}dial_result`;

interface HandoffGroup {
  key: string;
  trigger: string | null;
  stages: ConversationLogDetail[];
}

type ActivityLogEntry =
  | { kind: "detail"; detail: ConversationLogDetail }
  | { kind: "handoff"; group: HandoffGroup };

const parseHandoffPayload = (
  payload: string | null,
): { attempt_id?: string; seq?: number; trigger?: string; occurred_at?: string } => {
  if (!payload) return {};
  try {
    const parsed = JSON.parse(payload);
    return parsed && typeof parsed === "object" ? parsed : {};
  } catch {
    return {};
  }
};

// Folds the handoff:* stages of each handoff attempt into one entry, placed
// where the attempt's first stage sits. Stages are ordered by the sequence the
// browser assigned, since the rows are written fire-and-forget and can land
// out of order. Stages logged by the Twilio webhook have no sequence: they
// follow the browser's, ordered by when each one happened.
const groupActivityLog = (details: ConversationLogDetail[]): ActivityLogEntry[] => {
  const entries: ActivityLogEntry[] = [];
  const groups = new Map<string, HandoffGroup>();
  const sequences = new Map<number, number>();
  const occurredAt = new Map<number, number>();

  for (const detail of details) {
    if (!detail.action?.startsWith(HANDOFF_ACTION_PREFIX)) {
      entries.push({ kind: "detail", detail });
      continue;
    }

    const { attempt_id, seq, trigger, occurred_at } = parseHandoffPayload(detail.payload);
    const key = attempt_id ?? "unknown";
    if (typeof seq === "number") sequences.set(detail.id, seq);
    const happenedAt = new Date(occurred_at ?? detail.created_at).getTime();
    occurredAt.set(detail.id, Number.isNaN(happenedAt) ? new Date(detail.created_at).getTime() : happenedAt);

    let group = groups.get(key);
    if (!group) {
      group = { key, trigger: trigger ?? null, stages: [] };
      groups.set(key, group);
      entries.push({ kind: "handoff", group });
    }
    group.stages.push(detail);
  }

  for (const group of groups.values()) {
    group.stages.sort(
      (a, b) =>
        (sequences.get(a.id) ?? Number.MAX_SAFE_INTEGER) - (sequences.get(b.id) ?? Number.MAX_SAFE_INTEGER) ||
        Number(a.action === DIAL_RESULT_ACTION) - Number(b.action === DIAL_RESULT_ACTION) ||
        (occurredAt.get(a.id) ?? 0) - (occurredAt.get(b.id) ?? 0),
    );
  }

  return entries;
};

const ExpandToggle = ({
  expanded,
  onToggle,
  label,
}: {
  expanded: boolean;
  onToggle: () => void;
  label: string;
}) => (
  <button
    type="button"
    onClick={(event) => {
      event.stopPropagation();
      onToggle();
    }}
    aria-expanded={expanded}
    aria-label={label}
    className="inline-flex h-11 w-11 items-center justify-center rounded text-genie-text-secondary hover:text-genie-primary hover:bg-genie-primary/10 transition-colors"
  >
    <ChevronDown className={cn("h-4 w-4 transition-transform", expanded && "rotate-180")} />
  </button>
);

const HandoffGroupRow = ({ group, expandAll = false }: { group: HandoffGroup; expandAll?: boolean }) => {
  const [expanded, setExpanded] = useState(expandAll);

  useEffect(() => {
    setExpanded(expandAll);
  }, [expandAll]);

  const { stages, trigger } = group;
  const hasFailure = stages.some((stage) => stage.status === "fail" || stage.status === "failed");
  const lastStage = stages[stages.length - 1].action?.slice(HANDOFF_ACTION_PREFIX.length);
  const description = [
    `${stages.length} ${stages.length === 1 ? "stage" : "stages"}`,
    trigger && `trigger: ${trigger}`,
    lastStage && `last: ${lastStage}`,
  ]
    .filter(Boolean)
    .join(" · ");

  return (
    <>
      <tr
        onClick={() => setExpanded((prev) => !prev)}
        className={cn(
          "border-b border-genie-border hover:bg-genie-background-muted transition-colors cursor-pointer",
          expanded && "bg-genie-background-muted",
        )}
      >
        <td className="px-2 py-3 text-sm text-genie-text-primary font-medium whitespace-nowrap">handoff</td>
        <td className="px-2 py-3 text-sm whitespace-nowrap">
          {getActivityLogStatusBadge(hasFailure ? "failed" : "success")}
        </td>
        <td className="px-2 py-3 text-sm text-genie-text-secondary">{description}</td>
        <td className="px-2 py-3 text-xs text-genie-text-secondary whitespace-nowrap">
          {new Date(stages[0].created_at).toLocaleString()}
        </td>
        <td className="px-1 w-11">
          <ExpandToggle
            expanded={expanded}
            onToggle={() => setExpanded((prev) => !prev)}
            label={expanded ? "Hide handoff stages" : "Show handoff stages"}
          />
        </td>
      </tr>
      {expanded &&
        stages.map((stage) => <ActivityLogRow key={stage.id} detail={stage} expandAll={expandAll} nested />)}
    </>
  );
};

const ActivityLogRow = ({
  detail,
  expandAll = false,
  nested = false,
}: {
  detail: ConversationLogDetail;
  expandAll?: boolean;
  nested?: boolean;
}) => {
  const [expanded, setExpanded] = useState(expandAll);
  const hasPayload = Boolean(detail.payload);
  const hasResult = Boolean(detail.result);
  const isExpandable = hasPayload || hasResult;
  const isExpanded = isExpandable && expanded;

  useEffect(() => {
    setExpanded(expandAll);
  }, [expandAll]);

  const getDescription = (
    action: string | null,
    status: string | null,
    payload: string | null,
    result: string | null,
  ) => {
    if (action?.startsWith(HANDOFF_ACTION_PREFIX)) return result || "—";

    switch (action) {
      case "system_context":
        if (status === "success") {
          return "System context successfully sent to agent config before the conversation start";
        } else if (status === "failed") {
          return "System context failed to send to agent config";
        }
        break;

      case "end":
      case "record":
        return payload || "—";

      case "connection_lost":
        return "The connection to ElevenLabs closed unexpectedly. Expand for the close code and browser state.";

      case "analysis":
        if (status === "started") {
          return `Analysis started${payload ? ": " + payload : ""}`;
        } else if (status === "completed") {
          return "Analysis completed successfully";
        } else if (status === "failed") {
          return "Analysis failed";
        }
        break;

      case "insights":
        if (status === "recorded") {
          return "Conversation Details recorded in the database - analysis available in the Insights table";
        } else if (status === "failed") {
          return "Conversation Details were not recorded to database";
        }
        break;

      case "whatsapp":
      case "email":
      case "slack":
      case "telegram":
      case "teams":
        return result || "—";
    }

    return "—";
  };

  return (
    <>
      <tr
        onClick={isExpandable ? () => setExpanded((prev) => !prev) : undefined}
        className={cn(
          "border-b border-genie-border hover:bg-genie-background-muted transition-colors",
          isExpandable && "cursor-pointer",
          isExpanded && "bg-genie-background-muted",
        )}
      >
        <td
          className={cn(
            "px-2 py-3 text-sm text-genie-text-primary font-medium whitespace-nowrap",
            nested && "pl-8",
          )}
        >
          {detail.action || "Unknown Action"}
        </td>
        <td className="px-2 py-3 text-sm whitespace-nowrap">
          {detail.status ? (
            getActivityLogStatusBadge(detail.status)
          ) : (
            <span className="text-genie-text-muted">—</span>
          )}
        </td>
        <td className="px-2 py-3 text-sm text-genie-text-secondary">
          {getDescription(detail.action, detail.status, detail.payload, detail.result)}
        </td>
        <td className="px-2 py-3 text-xs text-genie-text-secondary whitespace-nowrap">
          {new Date(detail.created_at).toLocaleString()}
        </td>
        <td className="px-1 w-11">
          {isExpandable && (
            <ExpandToggle
              expanded={isExpanded}
              onToggle={() => setExpanded((prev) => !prev)}
              label={isExpanded ? "Hide payload and result" : "Show payload and result"}
            />
          )}
        </td>
      </tr>
      {isExpanded && (
        <tr className="border-b border-genie-border bg-genie-background-muted">
          <td colSpan={5} className="px-4 py-3">
            <div className={cn("grid gap-4", hasPayload && hasResult && "md:grid-cols-2")}>
              {hasPayload && (
                <div className="min-w-0">
                  <CopyableJsonBlock label="Payload" value={detail.payload} />
                </div>
              )}
              {hasResult && (
                <div className="min-w-0">
                  <CopyableJsonBlock label="Result" value={detail.result} />
                </div>
              )}
            </div>
          </td>
        </tr>
      )}
    </>
  );
};

export const ConversationLogDetailsSidebar = ({
  isOpen,
  onClose,
  selectedLog,
  getStatusBadge,
  showRawDataModal: externalShowRawDataModal = false,
  onShowRawDataModalChange,
}: ConversationLogDetailsSidebarProps) => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [expandAllDetails, setExpandAllDetails] = useState(false);
  const [isCreatingGenie, setIsCreatingGenie] = useState(false);
  const [showRawDataModal, setShowRawDataModal] = useState(externalShowRawDataModal);
  const [fetchConvId, setFetchConvId] = useState<string | null>(null);
  const { data: details, isLoading: detailsLoading } = useConversationLogDetails(
    selectedLog?.id || 0,
  );
  const activityLogEntries = useMemo(() => groupActivityLog(details?.data ?? []), [details?.data]);
  const {
    data: elevenLabsData,
    isLoading: elevenLabsLoading,
    isError: elevenLabsFailed,
  } = useElevenLabsConversationData(fetchConvId);
  const { analyzeConversation, createGenieFromAnalysis } = useConversationAnalysis();
  const [activeTab, setActiveTab] = useState<DetailsTab>("activity");

  // Turns that only carry injected session context have neither a message nor a tool call.
  const transcriptLines = useMemo<TranscriptLine[]>(() => {
    const isLoadedForSelectedLog = Boolean(fetchConvId) && fetchConvId === selectedLog?.el_conv_id;
    if (!isLoadedForSelectedLog || !Array.isArray(elevenLabsData?.transcript)) return [];
    return elevenLabsData.transcript.filter(
      (line: TranscriptLine | null) => line?.message || line?.tool_calls?.length,
    );
  }, [elevenLabsData, fetchConvId, selectedLog?.el_conv_id]);

  useEffect(() => {
    setActiveTab("activity");
  }, [selectedLog?.id]);

  const handleTabChange = (tab: string) => {
    setActiveTab(tab as DetailsTab);
    if (tab === "transcript") {
      setFetchConvId(selectedLog?.el_conv_id || null);
    }
  };

  useEffect(() => {
    setShowRawDataModal(externalShowRawDataModal);
    if (externalShowRawDataModal && selectedLog?.el_conv_id) {
      setFetchConvId(selectedLog.el_conv_id);
    }
  }, [externalShowRawDataModal, selectedLog?.el_conv_id]);

  const handleFetchRawData = () => {
    if (!selectedLog?.el_conv_id) {
      toast.error("No conversation ID available");
      return;
    }

    setFetchConvId(selectedLog.el_conv_id);
    setShowRawDataModal(true);
    if (onShowRawDataModalChange) {
      onShowRawDataModalChange(true);
    }
  };

  const handleCopyRawData = async () => {
    if (!elevenLabsData) {
      toast.error("No data to copy");
      return;
    }

    try {
      await navigator.clipboard.writeText(JSON.stringify(elevenLabsData, null, 2));
      toast.success("Copied to clipboard");
    } catch (error: any) {
      toast.error("Failed to copy to clipboard");
    }
  };

  const isSetupConversation = selectedLog?.conversation_type === "setup";
  const isNotCompleted = selectedLog?.conversation_status !== "completed";
  const isCompleted = selectedLog?.conversation_status === "completed";
  const shouldShowCreateButton = isSetupConversation && isNotCompleted;
  const shouldShowCheckGenieButton =
    isSetupConversation && isCompleted && selectedLog?.created_agent_id;

  const handleCreateGenie = async () => {
    if (!selectedLog?.el_conv_id) {
      toast.error("No conversation ID available");
      return;
    }

    try {
      setIsCreatingGenie(true);

      // Resolve conversation author's user ID
      const authorUserId =
        await ConversationLogsService.resolveConversationAuthorUserId(selectedLog);

      const analysis = await analyzeConversation([], selectedLog.el_conv_id, authorUserId);
      await createGenieFromAnalysis(analysis);

      await queryClient.invalidateQueries({ queryKey: ["conversation-logs"] });
      onClose();
    } catch (error: any) {
      toast.error(error.message || "Failed to create genie from conversation");
    } finally {
      setIsCreatingGenie(false);
    }
  };

  const handleCheckGenie = () => {
    if (!selectedLog?.created_agent_id) {
      toast.error("No genie ID found");
      return;
    }
    navigate(`/genie-hub/my-genies/${selectedLog.created_agent_id}`);
  };

  return (
    <>
      <SideDrawer
        isOpen={isOpen}
        onClose={onClose}
        width="min(1400px, 85vw)"
        title="Conversation Log Details"
        branding={{ primaryColor: "#4E9CFF" }}
      >
        {selectedLog && (
          <div className="py-6 space-y-6">
          {/* Conversation Log Data Tiles */}
          <div>
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-genie-text-primary">
                Conversation Details
              </h3>
              <div className="flex gap-2">
                <Button
                  onClick={handleFetchRawData}
                  variant="outline"
                  size="sm"
                  className="gap-2"
                  disabled={elevenLabsLoading}
                >
                  <Code className="h-4 w-4" />
                  {elevenLabsLoading ? "Loading..." : "Raw Data"}
                </Button>
                {shouldShowCreateButton && (
                  <Button
                    onClick={handleCreateGenie}
                    disabled={isCreatingGenie}
                    className="bg-genie-primary hover:bg-genie-primary/90 text-white gap-2 h-auto py-2 px-3"
                    size="sm"
                  >
                    <Wand2 className="h-4 w-4" />
                    {isCreatingGenie ? "Creating genie..." : "Create genie"}
                  </Button>
                )}
                {shouldShowCheckGenieButton && (
                  <Button
                    onClick={handleCheckGenie}
                    className="bg-genie-primary hover:bg-genie-primary/90 text-white gap-2 h-auto py-2 px-3"
                    size="sm"
                  >
                    <ExternalLink className="h-4 w-4" />
                    Check Genie
                  </Button>
                )}
              </div>
            </div>
            <div className="space-y-3">
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
                <div className="bg-genie-background-muted rounded-lg p-4 border border-genie-border">
                  <p className="text-xs text-genie-text-secondary mb-1">Agent</p>
                  <p className="text-sm font-medium text-genie-text-primary break-words">
                    <AgentNameCell name={selectedLog.agent_name} logoUrl={selectedLog.agent_logo_url} />
                  </p>
                </div>
                <div className="bg-genie-background-muted rounded-lg p-4 border border-genie-border">
                  <p className="text-xs text-genie-text-secondary mb-1">Status</p>
                  {getStatusBadge(selectedLog.status)}
                </div>
                <div className="bg-genie-background-muted rounded-lg p-4 border border-genie-border">
                  <p className="text-xs text-genie-text-secondary mb-1">Email</p>
                  <p className="text-sm font-medium text-genie-text-primary break-all">
                    <CopyableEmail email={selectedLog.email} />
                  </p>
                </div>
                <div className="bg-genie-background-muted rounded-lg p-4 border border-genie-border">
                  <p className="text-xs text-genie-text-secondary mb-1">User ID</p>
                  <p className="text-xs font-mono text-genie-text-primary break-all">
                    {selectedLog.user_id || "—"}
                  </p>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="bg-genie-background-muted rounded-lg p-4 border border-genie-border">
                  <p className="text-xs text-genie-text-secondary mb-2">ElevenLabs IDs</p>
                  <div className="space-y-2">
                    <p className="text-xs font-mono text-genie-text-primary break-all">
                      {selectedLog.el_conv_id || "—"}
                    </p>
                    {selectedLog.el_agent_id && (
                      <p className="text-xs font-mono text-genie-text-primary break-all">
                        {selectedLog.el_agent_id}
                      </p>
                    )}
                  </div>
                </div>
                <div className="bg-genie-background-muted rounded-lg p-4 border border-genie-border">
                  <p className="text-xs text-genie-text-secondary mb-3">Timestamps</p>
                  <div className="space-y-1 text-xs font-mono text-genie-text-primary">
                    <div className="flex justify-between gap-2">
                      <span className="text-genie-text-secondary">Created</span>
                      <span>
                        {new Date(selectedLog.created_at).toLocaleString("de-DE").replace(",", "")}
                      </span>
                    </div>
                    {selectedLog.updated_at && (
                      <div className="flex justify-between gap-2">
                        <span className="text-genie-text-secondary">Updated</span>
                        <span>
                          {new Date(selectedLog.updated_at)
                            .toLocaleString("de-DE")
                            .replace(",", "")}
                        </span>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>

          <Tabs value={activeTab} onValueChange={handleTabChange}>
            <TabsList>
              <TabsTrigger value="activity">Activity</TabsTrigger>
              <TabsTrigger value="transcript">Transcript</TabsTrigger>
            </TabsList>

            <TabsContent value="activity" className="mt-4 space-y-6">
          {/* Activity Log Table */}
          {!detailsLoading && details && details.data.length > 0 && (
            <div>
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-genie-text-primary">
                  Activity Log ({details.data.length})
                </h3>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setExpandAllDetails(!expandAllDetails)}
                >
                  {expandAllDetails ? "Collapse all" : "Expand all"}
                </Button>
              </div>
              <div className="border border-genie-border rounded-lg overflow-x-auto">
                <table className="w-full">
                  <thead className="sticky top-0 z-10">
                    <tr className="border-b border-genie-border bg-genie-background-muted">
                      <th className="px-2 py-3 text-left text-xs font-semibold text-genie-text-secondary whitespace-nowrap">
                        Action
                      </th>
                      <th className="px-2 py-3 text-left text-xs font-semibold text-genie-text-secondary whitespace-nowrap">
                        Status
                      </th>
                      <th className="px-2 py-3 text-left text-xs font-semibold text-genie-text-secondary whitespace-nowrap">
                        Description
                      </th>
                      <th className="px-2 py-3 text-left text-xs font-semibold text-genie-text-secondary whitespace-nowrap">
                        Created
                      </th>
                      <th className="px-1 w-11">
                        <span className="sr-only">Details</span>
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {activityLogEntries.map((entry) =>
                      entry.kind === "handoff" ? (
                        <HandoffGroupRow
                          key={`handoff-${entry.group.key}`}
                          group={entry.group}
                          expandAll={expandAllDetails}
                        />
                      ) : (
                        <ActivityLogRow
                          key={entry.detail.id}
                          detail={entry.detail}
                          expandAll={expandAllDetails}
                        />
                      ),
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {detailsLoading && (
            <div className="text-center py-8">
              <RefreshCw className="h-6 w-6 animate-spin mx-auto mb-2 text-genie-primary" />
              <p className="text-sm text-genie-text-secondary">Loading activity details...</p>
            </div>
          )}

          {!detailsLoading && (!details || details.data.length === 0) && (
            <div className="bg-genie-background-muted rounded-lg p-6 text-center">
              <p className="text-sm text-genie-text-muted">No activity details available</p>
            </div>
          )}

          {/* Metadata */}
          {selectedLog.metadata && (
            <div>
              <h3 className="text-lg font-semibold mb-3 text-genie-text-primary">Metadata</h3>
              <div className="bg-genie-background-muted rounded-lg p-4">
                <pre className="text-xs whitespace-pre-wrap break-words text-genie-text-primary overflow-x-auto">
                  {JSON.stringify(selectedLog.metadata, null, 2)}
                </pre>
              </div>
            </div>
          )}
            </TabsContent>

            <TabsContent value="transcript" className="mt-4">
              {elevenLabsLoading ? (
                <div className="text-center py-8">
                  <RefreshCw className="h-6 w-6 animate-spin mx-auto mb-2 text-genie-primary" />
                  <p className="text-sm text-genie-text-secondary">Loading transcript...</p>
                </div>
              ) : elevenLabsFailed ? (
                <div className="bg-genie-background-muted rounded-lg p-6 text-center">
                  <p className="text-sm text-genie-text-muted">
                    The transcript could not be loaded from ElevenLabs
                  </p>
                </div>
              ) : (
                <div className="space-y-3">
                  {transcriptLines.length > 0 && (
                    <div className="flex justify-end">
                      <TranscriptCopyButton
                        transcript={transcriptLines}
                        genieName={selectedLog.agent_name ?? undefined}
                        dateRaw={selectedLog.created_at}
                        durationSeconds={elevenLabsData?.metadata?.call_duration_secs}
                      />
                    </div>
                  )}
                  <TranscriptTab
                    transcript={transcriptLines}
                    genieName={selectedLog.agent_name ?? undefined}
                    genieLogoUrl={selectedLog.agent_logo_url}
                  />
                </div>
              )}
            </TabsContent>
          </Tabs>
        </div>
      )}
      </SideDrawer>

      {/* Raw Data Modal - Outside SideDrawer */}
      <Dialog open={showRawDataModal} onOpenChange={(open) => {
        setShowRawDataModal(open);
        if (onShowRawDataModalChange) {
          onShowRawDataModalChange(open);
        }
      }}>
        <DialogContent className="w-[95vw] h-[95vh] max-w-none flex flex-col">
          <DialogHeader className="flex flex-row items-center justify-between">
            <DialogTitle className="flex items-center gap-2">
              <Code className="h-5 w-5" />
              ElevenLabs Conversation Response
            </DialogTitle>
            <DialogDescription className="sr-only">
              Raw ElevenLabs conversation data viewer
            </DialogDescription>
            <Button
              onClick={handleCopyRawData}
              variant="outline"
              size="sm"
              disabled={!elevenLabsData || elevenLabsLoading}
            >
              <Copy className="h-4 w-4" />
            </Button>
          </DialogHeader>
          <div className="flex-1 overflow-auto">
            {elevenLabsLoading ? (
              <div className="flex items-center justify-center py-8">
                <RefreshCw className="h-6 w-6 animate-spin text-genie-primary" />
              </div>
            ) : elevenLabsData ? (
              <div className="bg-genie-background-muted rounded-lg p-4 border border-genie-border">
                <pre className="text-xs whitespace-pre-wrap break-words text-genie-text-primary overflow-x-auto">
                  {JSON.stringify(elevenLabsData, null, 2)}
                </pre>
              </div>
            ) : (
              <div className="text-center py-8 text-genie-text-secondary">No data available</div>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};
